import {
  IActionReference,
  IAvailableWorkflowActions,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IRoleType,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { DeepPartial } from '@publishing-engine/domain/types';
import { IPeerReviewManagementView } from '@publishing-engine/domain/views';
import {
  IGetActiveRevisionResponse,
  IGetSubmissionFieldsResponse,
  IGetSubmissionWorkflowResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchActiveRevision,
  fetchFormDraft,
  fetchFormSchema,
  fetchOrganizationAndJournalsInformation,
  fetchReview,
  fetchReviewerPastReviewHistory,
  fetchReviewExtensionDeadline,
  fetchSubmissionContext,
  fetchSubmissionFields,
  fetchSubmissionWorkflow,
  IFetchOrganizationAndJournalsInfo
} from 'src/api/queries';
import { useKeycloak } from 'src/components/AuthHOC';
import { IPastRevisionHistoryResponse } from 'src/pages/Review/PastReviewTab';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError, isUnauthorizedError } from 'src/utils/queryClient';

type ReviewContextData = {
  fieldsResponse: IGetSubmissionFieldsResponse;
  review: IPeerReviewManagementView;
  workflowResponse: IGetSubmissionWorkflowResponse;
  availableActions: IAvailableWorkflowActions;
  reviewerFormSchema: {
    documentSchema: IFormSchema;
  };
  revisionFormSchema: {
    documentSchema: IFormSchema;
  };
  invalidateReviewContextData: () => void;
  submissionContext: ISubmissionContext;
  organizationData: IFetchOrganizationAndJournalsInfo;
  submissionId: string;
  organizationId: string;
  firstAssessmentFormDraft: DeepPartial<IPeerReviewFirstAssessment> | undefined;
  revisionFormDraft: DeepPartial<IPeerReviewRevisionAssessment> | undefined;
  activeRevisionData?: IGetActiveRevisionResponse | null;
  reviewExtensionDeadline:
    | {
        currentState: string;
        currentDeadline: string;
        nextDeadline: string;
        deadlineExtensionInDays: number;
      }
    | undefined;
  refetchReview: () => void;
  hasSubmittedReviewInCurrentRound: () => boolean;
  isReviewRequestAccepted: () => boolean;
  reviewerOrCoReviewer: 'reviewer' | 'coReviewer';
  otherCoReviewer?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    affiliation?: string;
    orcid?: string;
    ringgoldId?: string;
  };
  pastReviewsData: IPastRevisionHistoryResponse;
};

/**
 *
 * @param workflowResponse
 * @param currentState
 * @returns Available actions on Review Workflow State
 */
function getAvailableWorkflowActions(
  workflowResponse: IGetSubmissionWorkflowResponse,
  currentState: string,
  roleContext: string
): IAvailableWorkflowActions {
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.review.states[currentState]
      ?.actions || [];

  return {
    contains: function (
      actionReference: IActionReference['name'],
      withRole?: boolean
    ) {
      return withRole
        ? actions.some(
            a => a.name === actionReference && a.roleType === roleContext
          )
        : actions.some(a => a.name === actionReference);
    }
  };
}

export const ReviewDataContext = createContext<ReviewContextData | undefined>(
  undefined
);

export const useReviewData = (): ReviewContextData => {
  const context = useContext(ReviewDataContext);
  if (!context) {
    throw new Error('useReviewData must be used within a ReviewDataProvider');
  }
  return context;
};

export const getReviewRequestContextFromRoute = () => {
  if (document.location.href.match('reviews')) {
    const { id: reviewId } = useParams();

    if (!reviewId) {
      throw new Error('Unable to retrieve reviewId from params');
    }

    return {
      roleContext: 'reviewer' as const,
      reviewId
    };
  }
  throw new Error('Unable to retrieve reviewId from params');
};

const ReviewDataProvider = ({ children }: { children: JSX.Element }) => {
  const { roleContext, reviewId } = getReviewRequestContextFromRoute();
  const queryClient = useQueryClient();
  const { currentUser } = useKeycloak();

  const reviewKey = ['review', { reviewId }];
  const reviewExtensionDeadlineKey = ['review-extension-deadline', reviewId];
  const {
    data: reviewData,
    isLoading: isLoadingReview,
    isError: isErrorReview,
    error: errorReview,
    refetch: refetchReview
  } = useQuery({
    queryKey: reviewKey,
    queryFn: () => fetchReview(reviewId)
  });

  const submissionFieldsQueryKey = ['submission-fields-review', reviewId];
  const {
    data: submissionFields,
    isLoading: isLoadingSubmissionFields,
    isError: isErrorFields
  } = useQuery({
    queryKey: submissionFieldsQueryKey,
    queryFn: () =>
      reviewData?.caseData.submission?.id
        ? fetchSubmissionFields(
            reviewData.caseData.submission.id,
            roleContext as IRoleType,
            reviewId
          )
        : null,
    enabled: !!reviewData?.caseData?.submission.id,
    retry: 0
  });

  const {
    data: submissionContext,
    isLoading: isLoadingContext,
    isError: isErrorContext
  } = useQuery({
    queryKey: ['submission-context', reviewData?.submissionContextId],
    queryFn: () =>
      reviewData?.submissionContextId
        ? fetchSubmissionContext(reviewData?.submissionContextId)
        : null
  });

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization
  } = useQuery({
    queryKey: [
      'organization',
      reviewData?.submissionContextId,
      submissionContext?.organizationId
    ],
    queryFn: () =>
      submissionContext?.organizationId
        ? fetchOrganizationAndJournalsInformation(
            submissionContext?.organizationId
          )
        : null
  });

  const workflowQueryKey = [
    'submission-workflow',
    reviewData?.caseData.submission.id
  ];
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    isError: isErrorWorkflow
  } = useQuery({
    queryKey: workflowQueryKey,
    queryFn: () =>
      reviewData?.caseData.submission.id
        ? fetchSubmissionWorkflow(
            reviewData?.caseData.submission.id,
            roleContext as IRoleType,
            reviewId
          )
        : null,
    enabled: !!reviewData?.caseData.submission.id,
    retry: 0
  });

  const firstAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.reviewerFirstAssessment;

  const revisionAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.reviewerRevisionAssessment;

  const reviewerOrCoReviewer =
    reviewData?.caseData.coReview?.coReviewer.id === currentUser.id
      ? 'coReviewer'
      : 'reviewer';

  const otherCoReviewer = reviewData?.caseData.coReview?.status
    ? reviewerOrCoReviewer === 'coReviewer'
      ? reviewData?.caseData.reviewer
      : reviewData?.caseData.coReview.coReviewer
    : undefined;

  const {
    data: reviewerFormSchema,
    isLoading: isLoadingReviewSchema,
    isError: isErrorReviewSchema
  } = useQuery({
    queryKey: firstAssessmentSchemaId
      ? [
          'review-context',
          reviewData?.submissionContextId,
          'schema',
          firstAssessmentSchemaId
        ]
      : [],
    queryFn: () =>
      reviewData?.submissionContextId && firstAssessmentSchemaId
        ? fetchFormSchema(
            reviewData?.submissionContextId,
            firstAssessmentSchemaId
          )
        : undefined
  });

  const {
    data: reviewExtensionDeadline,
    isLoading: isLoadingReviewExtensionDeadline,
    isError: isErrorReviewExtensionDeadline
  } = useQuery({
    queryKey: reviewExtensionDeadlineKey,
    queryFn: () => fetchReviewExtensionDeadline(reviewId, 'reviewer')
  });

  const {
    data: revisionFormSchema,
    isLoading: isLoadingRevisionSchema,
    isError: isErrorRevisionSchema
  } = useQuery({
    queryKey: revisionAssessmentSchemaId
      ? [
          'review-context',
          reviewData?.submissionContextId,
          'schema',
          revisionAssessmentSchemaId
        ]
      : [],
    queryFn: () =>
      reviewData?.submissionContextId && revisionAssessmentSchemaId
        ? fetchFormSchema(
            reviewData?.submissionContextId,
            revisionAssessmentSchemaId
          )
        : undefined
  });

  const {
    data: firstAssessmentFormDraft,
    isLoading: isLoadingFirstAssessmentFormDraft
  } = useQuery({
    queryKey: ['form-draft', reviewId, 'review', 'reviewerFirstAssessment'],
    queryFn: () =>
      reviewId
        ? fetchFormDraft(reviewId, 'review', 'reviewerFirstAssessment')
        : undefined
  });

  const { data: revisionFormDraft, isLoading: isLoadingRevisionFormDraft } =
    useQuery({
      queryKey: [
        'form-draft',
        reviewId,
        'review',
        'reviewerRevisionAssessment'
      ],
      queryFn: () =>
        reviewId
          ? fetchFormDraft(reviewId, 'review', 'reviewerRevisionAssessment')
          : undefined
    });

  const { data: activeRevisionData, isLoading: isLoadingActiveRevision } =
    useQuery({
      queryKey: ['active-revision', reviewData?.caseData.submission.id],
      queryFn: () =>
        reviewData?.caseData.submission.id
          ? fetchActiveRevision(
              reviewData.caseData.submission.id,
              roleContext as IRoleType,
              reviewId
            )
          : null,
      enabled: !!reviewData?.caseData.submission.id,
      retry: 0
    });

  const { data: pastReviewsData, isLoading: isLoadingPastReview } = useQuery({
    queryKey: ['past-review-data', reviewData?.caseData.submission.id],
    queryFn: () =>
      reviewData?.caseData.submission.id
        ? fetchReviewerPastReviewHistory(
            reviewData.caseData.submission.id,
            roleContext as IRoleType,
            reviewId
          )
        : null,
    enabled: !!reviewData?.caseData.submission.id,
    retry: 0
  });

  if (!isLoadingReview && isErrorReview) {
    return isUnauthorizedError(errorReview) || isForbiddenError(errorReview)
      ? <Forbidden />
      : <NotFound />;
  }

  if (
    isLoadingReview ||
    isLoadingSubmissionFields ||
    isLoadingWorkflow ||
    isLoadingContext ||
    isLoadingOrganization ||
    isLoadingReviewSchema ||
    isLoadingRevisionSchema ||
    isLoadingFirstAssessmentFormDraft ||
    isLoadingRevisionFormDraft ||
    isLoadingActiveRevision ||
    isLoadingReviewExtensionDeadline ||
    isLoadingPastReview
  ) {
    return <Loading />;
  }

  const isError =
    isErrorFields ||
    isErrorContext ||
    isErrorOrganization ||
    isErrorWorkflow ||
    isErrorReview ||
    isErrorReviewSchema ||
    isErrorRevisionSchema ||
    isErrorReviewExtensionDeadline;

  const isDataEmpty =
    !reviewData ||
    !submissionFields ||
    !submissionContext ||
    !organizationData ||
    !reviewerFormSchema ||
    !revisionFormSchema ||
    !workflowData ||
    !reviewExtensionDeadline ||
    !pastReviewsData;

  if (isError || isDataEmpty) {
    return isForbiddenError(errorReview) ? <Forbidden /> : <NotFound />;
  }

  const {
    caseData: {
      submission: { id: submissionId }
    }
  } = reviewData;

  const invalidateReviewContext = () => {
    queryClient.invalidateQueries({ queryKey: reviewKey });
    queryClient.invalidateQueries({ queryKey: reviewExtensionDeadlineKey });
    queryClient.invalidateQueries({ queryKey: submissionFieldsQueryKey });
  };

  const hasSubmittedReviewInCurrentRound = () => {
    // Check if there's actual assessment data (not just empty objects)
    const isRevisionRound = !!activeRevisionData;
    if (isRevisionRound) {
        const hasRevisionAssessment = !!(
            reviewData.caseData.revisionAssessment &&
            Object.keys(reviewData.caseData.revisionAssessment).length > 0
        );
        return hasRevisionAssessment
    }

    const hasFirstAssessment = !!(
        reviewData.caseData.firstAssessment &&
        Object.keys(reviewData.caseData.firstAssessment).length > 0
    );

    return hasFirstAssessment;
  };

  const isReviewRequestAccepted = () => {
    return reviewData?.caseData?.acceptance === 'accept' 
    || reviewData?.caseData?.acceptance === 'autoAccept'
    || !!reviewData?.caseData?.isBoardMember;
  };

  return (
    <ReviewDataContext.Provider
      value={{
        review: reviewData,
        fieldsResponse: submissionFields,
        submissionId,
        workflowResponse: workflowData,
        availableActions: getAvailableWorkflowActions(
          workflowData,
          reviewData.workflow.currentState,
          roleContext
        ),
        organizationData: organizationData,
        submissionContext: submissionContext,
        organizationId: submissionContext.organizationId,
        reviewerFormSchema: reviewerFormSchema,
        revisionFormSchema: revisionFormSchema,
        firstAssessmentFormDraft:
          firstAssessmentFormDraft?.data as DeepPartial<IPeerReviewFirstAssessment>,
        revisionFormDraft:
          revisionFormDraft?.data as DeepPartial<IPeerReviewRevisionAssessment>,
        activeRevisionData: activeRevisionData,
        invalidateReviewContextData: invalidateReviewContext,
        reviewExtensionDeadline,
        refetchReview,
        hasSubmittedReviewInCurrentRound,
        isReviewRequestAccepted,
        reviewerOrCoReviewer,
        otherCoReviewer,
        pastReviewsData
      }}
    >
      {children}
    </ReviewDataContext.Provider>
  );
};

export default ReviewDataProvider;
